import React, { useRef, useState, useEffect } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import { Btn, fmtDate } from '../../components/ui.jsx';
import { openPrintWindow, resolveAssetUrl } from '../../utils/printWindow.js';
import { fetchGuest, fetchFolio, fetchReservationGuests } from '../../services/api.js';

/**
 * Guest Registration Form — the paper every hotel hands the guest at
 * departure to review and sign (and the receptionist co-signs), modelled
 * 1:1 on the property's existing bilingual (FR/EN) paper form: same field
 * labels, same order, same NOTICE TO GUESTS / CHECK-IN-OUT wording block,
 * same two signature lines.
 *
 * Every field that exists in the system is pre-filled — room, dates, rate,
 * nationality, ID number, meal plan, payment mode, additional persons in
 * the same reservation — so staff only need to print, then have the guest
 * and the receptionist sign. Hotel identity (name, logo, TIN, address,
 * phone, email, website) comes from `property`, exactly like the Invoice
 * and Payment Request — nothing here is hard-coded per business.
 */
export default function GuestRegistrationPrint({ reservation, property, onClose }) {
  const printRef = useRef();
  const [guest,            setGuest]            = useState(null);   // idType/idNumber live on the PMS Guest record
  const [folio,            setFolio]            = useState(null);   // to infer the payment mode actually used
  const [includedPersons,  setIncludedPersons]  = useState([]);      // additional people under the same reservation

  useEffect(() => {
    if (reservation?.guestId) fetchGuest(reservation.guestId).then(setGuest).catch(() => setGuest(null));
    if (reservation?.id) {
      fetchFolio(reservation.id).then(setFolio).catch(() => setFolio(null));
      fetchReservationGuests(reservation.id).then(d => setIncludedPersons(Array.isArray(d) ? d : []))
        .catch(() => setIncludedPersons([]));
    }
  }, [reservation?.guestId, reservation?.id]);

  const idType   = guest?.idType || null;
  const idNumber = guest?.idNumber || null;
  const nationality = reservation?.guestNationality || guest?.nationality || '';

  const nights = reservation?.numNights
    ?? (reservation?.arrivalDate && reservation?.departureDate
      ? Math.max(1, Math.round((new Date(reservation.departureDate) - new Date(reservation.arrivalDate)) / 86400000))
      : null);

  // Latest non-voided/non-reversed payment on this folio, to pre-check the payment-mode box.
  const lastPaymentMethod = (folio?.payments || [])
    .filter(p => !p.reversed)
    .sort((a, b) => new Date(b.paidAt || 0) - new Date(a.paidAt || 0))[0]?.method || null;
  const paidBy = {
    cash:  lastPaymentMethod === 'cash',
    cheque: false, // not a payment method this system records yet — left for staff to tick manually
    card:  lastPaymentMethod === 'visa',
    momo:  lastPaymentMethod === 'mtn_momo' || lastPaymentMethod === 'airtel',
  };

  const meal = reservation?.mealPlan || null;
  const mealChecked = {
    fb: meal === 'fb',
    hb: meal === 'hb',
    bb: meal === 'bb',
    ro: meal === 'room_only',
  };

  const today = new Date();
  const todayLong = today.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const doPrint = () => {
    const ok = openPrintWindow({
      title: `Guest Registration Form — ${reservation?.confirmationNumber || ''}`,
      bodyHtml: printRef.current.innerHTML,
      format: 'letter',
    });
    if (!ok) alert('Your browser blocked the print popup. Please allow popups for this site and try again.');
  };

  /* ── shared style tokens (same as Invoice / Payment Request) ── */
  const cellLabel = {
    border: `1px solid ${T.navy}`, padding: '7px 10px', fontSize: 10.5,
    fontWeight: 800, color: T.navy, background: T.surface, width: '38%', verticalAlign: 'top', lineHeight: 1.35,
  };
  const cellValue = {
    border: `1px solid ${T.navy}`, padding: '7px 10px', fontSize: 12, fontWeight: 700, color: T.slate, verticalAlign: 'top',
  };
  const check = on => (
    <span style={{
      display: 'inline-block', width: 11, height: 11, border: `1.5px solid ${T.navy}`, marginRight: 6,
      verticalAlign: 'middle', textAlign: 'center', lineHeight: '10px', fontSize: 9, fontWeight: 900,
      color: T.navy, background: on ? T.goldLight : 'transparent',
    }}>{on ? '✓' : ''}</span>
  );

  return (
    <div>
      <div ref={printRef} style={{
        fontFamily: FONTS.body, color: T.navy, background: T.white,
        maxWidth: 720, margin: '0 auto', padding: '28px 30px',
        border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: SHADOW.lg,
      }}>

        {/* Header: property info + logo — identical pattern to the Invoice */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 18, marginBottom: 14 }}>
          <div>
            <p style={{ fontFamily: FONTS.display, fontSize: 19, fontWeight: 700, color: T.navy, marginBottom: 3 }}>
              {property?.name || 'Property Name'}
            </p>
            {property?.type && (
              <p style={{ fontSize: 10.5, fontWeight: 700, color: T.teal, letterSpacing: .6, textTransform: 'uppercase', marginBottom: 4 }}>
                {property.type}
              </p>
            )}
            {property?.addressLine1 && <p style={{ fontSize: 11, color: T.slate, lineHeight: 1.5 }}>{property.addressLine1}</p>}
            {property?.addressLine2 && <p style={{ fontSize: 11, color: T.slate, lineHeight: 1.5 }}>{property.addressLine2}</p>}
            <p style={{ fontSize: 11, color: T.slate, lineHeight: 1.5 }}>
              {[property?.city, property?.country].filter(Boolean).join(', ')}
            </p>
            {property?.postcode && <p style={{ fontSize: 11, color: T.slate, lineHeight: 1.5 }}>B.P.: {property.postcode}</p>}
            {property?.phone && <p style={{ fontSize: 11, color: T.slate, lineHeight: 1.5 }}>Tel: {property.phone}</p>}
            {property?.email && <p style={{ fontSize: 11, color: T.slate, lineHeight: 1.5 }}>{property.email}</p>}
            {property?.website && <p style={{ fontSize: 11, color: T.slate, lineHeight: 1.5 }}>{property.website}</p>}
            {property?.tin && (
              <p style={{ fontSize: 11, fontWeight: 800, color: T.navy, marginTop: 5 }}>TIN: {property.tin}</p>
            )}
          </div>
          {property?.logoUrl && (
            <img src={resolveAssetUrl(property.logoUrl)} alt="" crossOrigin="anonymous"
              style={{ maxWidth: 120, maxHeight: 90, objectFit: 'contain' }} />
          )}
        </div>

        {/* Title bar */}
        <div style={{ background: '#BFEFEA', textAlign: 'center', padding: '6px 0', marginBottom: 16 }}>
          <p style={{ fontFamily: FONTS.display, fontSize: 15, fontWeight: 800, color: T.navy, letterSpacing: 1.2 }}>
            GUEST REGISTRATION FORM
          </p>
        </div>

        {/* Main field table — identical labels/order to the paper form */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
          <tbody>
            <tr>
              <td style={cellLabel}>NOMS/NAMES</td>
              <td style={cellValue}>
                {reservation?.guestName || '—'}
                {includedPersons.length > 0 && (
                  <span style={{ display: 'block', fontSize: 10, color: T.muted, fontWeight: 600, marginTop: 2 }}>
                    + {includedPersons.map(p => p.fullName).join(', ')} (same room/reservation)
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td style={cellLabel}>CHAMBRE N° / ROOM N°<br />TYPE DE CHAMBRE / ROOM TYPE</td>
              <td style={cellValue}>
                {reservation?.roomNumber ? `Room ${reservation.roomNumber}` : '—'}
                {reservation?.roomTypeName ? ` — ${reservation.roomTypeName}` : ''}
              </td>
            </tr>
            <tr>
              <td style={cellLabel}>NOMBRE DE PERSONNES / NO. OF PAX<br />
                <span style={{ fontWeight: 600, fontSize: 9.5 }}>Adultes/Adults &nbsp;•&nbsp; Enfants/Children</span>
              </td>
              <td style={cellValue}>
                Adults: {reservation?.adults ?? '—'} &nbsp;&nbsp; Children: {reservation?.children ?? 0}
              </td>
            </tr>
            <tr>
              <td style={cellLabel}>ROOM RATE PER NIGHT</td>
              <td style={cellValue}>
                {reservation?.ratePerNight != null
                  ? `${Number(reservation.ratePerNight).toLocaleString()} ${reservation?.currency || property?.currency || ''}`
                  : '—'}
              </td>
            </tr>
            <tr>
              <td style={cellLabel}>DATE D'ARRIVEE / ARRIVAL DATE<br />DATE DE DEPART / DEPARTURE DATE</td>
              <td style={cellValue}>
                {fmtDate(reservation?.arrivalDate)} &nbsp;→&nbsp; {fmtDate(reservation?.departureDate)}
              </td>
            </tr>
            <tr>
              <td style={cellLabel}>DUREE DE SEJOUR / LENGTH OF STAY</td>
              <td style={cellValue}>{nights != null ? `${nights} night${nights > 1 ? 's' : ''}` : '—'}</td>
            </tr>
            <tr>
              <td style={cellLabel}>NATIONALITE / NATIONALITY</td>
              <td style={cellValue}>{nationality || '—'}</td>
            </tr>
            <tr>
              <td style={cellLabel}>NUMERO DU PASSEPORT / PASSPORT NUMBER<br />/ ID NUMBER</td>
              <td style={cellValue}>
                {idNumber ? `${idType ? cap(idType) + ': ' : ''}${idNumber}` : '—'}
              </td>
            </tr>
            <tr>
              <td style={cellLabel}>EMAIL</td>
              <td style={cellValue}>{reservation?.guestEmail || '—'}</td>
            </tr>
            <tr>
              <td style={cellLabel}>TELEPHONE</td>
              <td style={cellValue}>{reservation?.guestPhone || '—'}</td>
            </tr>
            <tr>
              <td style={cellLabel}>MODE DE PAIEMENT / PAYMENT MODE</td>
              <td style={{ ...cellValue, fontSize: 11 }}>
                <div>{check(paidBy.cash)}PAR CASH/BY CASH</div>
                <div>{check(paidBy.cheque)}PAR CHEQUE/BY CHEQUE</div>
                <div>{check(paidBy.card)}PAR CARTE/BY CARD</div>
                <div>{check(paidBy.momo)}MOMO</div>
              </td>
            </tr>
            <tr>
              <td style={cellLabel}>PLAN DE REPAS / MEAL PLAN</td>
              <td style={{ ...cellValue, fontSize: 11 }}>
                <div>{check(mealChecked.fb)}FULL BOARD</div>
                <div>{check(mealChecked.hb)}HALF BOARD</div>
                <div>{check(mealChecked.bb)}BED &amp; BREAKFAST</div>
                <div>{check(mealChecked.ro)}BED ONLY</div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Notices — same wording as the paper form; check-in/out times come from property config */}
        <div style={{ marginTop: 18 }}>
          <p style={{ fontSize: 9.5, color: T.slate, lineHeight: 1.6, marginBottom: 8 }}>
            <strong>NOTICE TO GUESTS:</strong> This is a privately owned property and the management reserves the right to
            provide services. The management will not be responsible for any accidents/injuries to guests or loss of
            money or valuables of any kind left in the room.
          </p>
          <p style={{ fontSize: 9.5, color: T.slate, lineHeight: 1.6, marginBottom: 14 }}>
            <strong>CHECK IN / CHECK-OUT TIME:</strong> Our check-in time is {property?.checkInTime || '2:00 p.m.'} and
            check-out time is {property?.checkOutTime || '10:00 a.m.'}. In the case of an extension, the hotel will
            automatically bill for an extension.
          </p>
        </div>

        {/* Signatures */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, marginTop: 10 }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 11, fontWeight: 800, color: T.navy, marginBottom: 26 }}>GUEST SIGNATURE:</p>
            <div style={{ borderTop: `1px solid ${T.border}` }} />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 11, fontWeight: 800, color: T.navy, marginBottom: 4 }}>FOR THE RECEPTION</p>
            <p style={{ fontSize: 10, color: T.muted, marginBottom: 22 }}>Done at {property?.city || property?.name || '—'}: {todayLong}</p>
            <div style={{ borderTop: `1px solid ${T.border}` }} />
          </div>
        </div>
      </div>{/* /printRef */}

      <div style={{ display: 'flex', gap: 10, marginTop: 20, maxWidth: 720, margin: '20px auto 0' }}>
        <Btn variant="secondary" onClick={onClose} style={{ flex: 1 }}>Close</Btn>
        <Btn variant="primary" onClick={doPrint} style={{ flex: 2 }}>🖨️ Print Guest Registration Form</Btn>
      </div>
    </div>
  );
}

function cap(s) { return s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : s; }
